import React, { useState } from 'react';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import { useCreateTaskTemplate } from '@hooks/api';
import { Task } from '@models/task.model';

interface CreateTaskTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedTaskIds: string[];
  projectId: string;
  tasks: Task[];
  onSuccess?: () => void;
}

export const CreateTaskTemplateModal: React.FC<CreateTaskTemplateModalProps> = ({
  isOpen,
  onClose,
  selectedTaskIds,
  projectId,
  tasks,
  onSuccess,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const createMutation = useCreateTaskTemplate();

  const selectedTasks = tasks.filter((t) => selectedTaskIds.includes(t.taskId!));
  const parentTaskCount = selectedTasks.filter((t) => !t.parentTaskId).length;
  const subtaskCount = selectedTasks.length - parentTaskCount;

  const handleSubmit = async () => {
    if (!name.trim()) return;

    try {
      await createMutation.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        taskIds: selectedTaskIds,
        projectId,
      });

      setName('');
      setDescription('');
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error('Error creating template:', error);
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create Task Template"
      confirmText="Create Template"
      handleConfirm={handleSubmit}
      isLoading={createMutation.isPending}
      disableConfirm={!name.trim()}>
      <div className="flex flex-col gap-4">
        <div className="p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600">
            You are creating a template with{' '}
            <span className="font-medium text-gray-900">
              {parentTaskCount} task{parentTaskCount !== 1 ? 's' : ''}
            </span>
            {subtaskCount > 0 && (
              <>
                {' '}
                and{' '}
                <span className="font-medium text-gray-900">
                  {subtaskCount} subtask{subtaskCount !== 1 ? 's' : ''}
                </span>
              </>
            )}
            .
          </p>
        </div>

        <FormGroup label="Template Name" htmlFor="templateName">
          <Input
            id="templateName"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter template name"
            autoFocus
          />
        </FormGroup>

        <FormGroup label="Description (optional)" htmlFor="templateDescription">
          <textarea
            id="templateDescription"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter a description for this template"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
            rows={3}
          />
        </FormGroup>

        <div className="mt-2">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Tasks:</h4>
          <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
            {selectedTasks
              .filter((t) => !t.parentTaskId)
              .map((task) => {
                const subtasks = selectedTasks.filter((t) => t.parentTaskId === task.taskId);
                return (
                  <div key={task.taskId} className="p-2 border-b border-gray-100 last:border-b-0">
                    <div className="text-sm text-gray-900">{task.name}</div>
                    {subtasks.length > 0 && (
                      <div className="ml-4 mt-1">
                        {subtasks.map((subtask) => (
                          <div key={subtask.taskId} className="text-xs text-gray-500">
                            └ {subtask.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CreateTaskTemplateModal;
